import httpClient from '@clients/http.client';
import { GmailIntegrationClientError } from './gmailIntegration.errors';
import {
  IGmailConnection,
  IGmailConnectionsResponse,
  IGmailInspectResponse,
  IGmailTarget,
} from './gmailIntegration.types';

const CONNECTIONS_PATH = '/me/integrations/gmail-addon/connections';
const INSPECT_PATH = '/integrations/gmail-addon/link-attempts/inspect';
const CONFIRM_PATH = '/integrations/gmail-addon/link-attempts/confirm';

function authenticatedConfig(authToken: string) {
  const token = typeof authToken === 'string' ? authToken.trim() : '';
  if (!token) {
    throw new GmailIntegrationClientError('unauthorized');
  }
  return { baseURL: 'https://2os45c8m2m.execute-api.eu-north-1.amazonaws.com/v1', headers: { Authorization: `Bearer ${token}` } };
}

function isSafeString(value: unknown, maxLength: number): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= maxLength;
}

function isIsoTimestamp(value: unknown): value is string {
  return isSafeString(value, 64) && !isNaN(Date.parse(value));
}

function parseTarget(value: unknown): IGmailTarget {
  const target = (value || {}) as Partial<IGmailTarget>;
  if (!isSafeString(target.id, 512) || !isSafeString(target.displayName, 500)) {
    throw new GmailIntegrationClientError('generic');
  }
  return { id: target.id, displayName: target.displayName };
}

function parseInspectResponse(value: unknown): IGmailInspectResponse {
  const response = (value || {}) as Partial<IGmailInspectResponse>;
  if (
    !isSafeString(response.googleEmail, 320) ||
    !isIsoTimestamp(response.expiresAt) ||
    !Array.isArray(response.targets) ||
    !(
      response.suggestedTargetId === undefined ||
      response.suggestedTargetId === null ||
      isSafeString(response.suggestedTargetId, 512)
    )
  ) {
    throw new GmailIntegrationClientError('generic');
  }
  return {
    googleEmail: response.googleEmail,
    expiresAt: response.expiresAt,
    targets: response.targets.map(parseTarget),
    ...(response.suggestedTargetId !== undefined
      ? { suggestedTargetId: response.suggestedTargetId }
      : {}),
  };
}

function parseConnectionsResponse(value: unknown): IGmailConnection[] {
  const response = (value || {}) as Partial<IGmailConnectionsResponse>;
  if (!Array.isArray(response.connections)) {
    throw new GmailIntegrationClientError('generic');
  }
  return response.connections.map((connectionValue: unknown) => {
    const connection = (connectionValue || {}) as Partial<IGmailConnection>;
    if (
      !isSafeString(connection.id, 512) ||
      !isSafeString(connection.googleEmail, 320) ||
      (connection.status !== 'connected' && connection.status !== 'needs_reconnect') ||
      !isIsoTimestamp(connection.connectedAt)
    ) {
      throw new GmailIntegrationClientError('generic');
    }
    return {
      id: connection.id,
      googleEmail: connection.googleEmail,
      target: parseTarget(connection.target),
      status: connection.status,
      connectedAt: connection.connectedAt,
    };
  });
}

export async function inspectGmailLinkAttempt(
  attempt: string,
  authToken: string
): Promise<IGmailInspectResponse> {
  const response = await httpClient.post(
    INSPECT_PATH,
    { attempt },
    authenticatedConfig(authToken)
  );
  return parseInspectResponse(response.data);
}

export async function confirmGmailLinkAttempt(
  attempt: string,
  targetId: string,
  authToken: string
): Promise<void> {
  await httpClient.post(
    CONFIRM_PATH,
    { attempt, targetId },
    authenticatedConfig(authToken)
  );
}

export async function getGmailConnections(authToken: string): Promise<IGmailConnection[]> {
  const response = await httpClient.get(CONNECTIONS_PATH, authenticatedConfig(authToken));
  return parseConnectionsResponse(response.data);
}

export async function disconnectGmailConnection(
  connectionId: string,
  authToken: string
): Promise<void> {
  if (!isSafeString(connectionId, 512)) {
    throw new GmailIntegrationClientError('generic');
  }
  await httpClient.delete(
    `${CONNECTIONS_PATH}/${encodeURIComponent(connectionId)}`,
    authenticatedConfig(authToken)
  );
}



// WEBPACK FOOTER //
// ./src/services/gmailIntegration/gmailIntegration.http.ts